import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Trash2, Eye, EyeOff, Pencil, Search, History, ArrowUp, ArrowDown, Power } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

const TYPE_OPTIONS = [
    { id: 'course', label: 'Course', color: 'bg-blue-500' },
    { id: 'addon', label: 'Add-on', color: 'bg-purple-500' },
    { id: 'upgrade', label: 'Upgrade', color: 'bg-amber-500' },
];

const VISIBILITY_OPTIONS = [
    { id: 'sales', label: 'Sales Only', color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { id: 'cs', label: 'CS Only', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    { id: 'both', label: 'Both (Sales + CS)', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
];

const EMPTY_FORM = {
    name: '', price: '', type: 'course', visibility: 'sales',
    commission_sales_executive: '',
    commission_team_leader: '',
    commission_sales_manager: '',
    commission_cs_agent: '',
    commission_cs_head: '',
    commission_mentor: '',
    commission_ceo_pool: '',
    commission_moa: '',
};

const fmtAED = (amt) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(amt || 0);

const calcTotal = (item) => {
    if (item.type === 'upgrade') {
        return (parseFloat(item.commission_cs_agent) || 0) +
               (parseFloat(item.commission_cs_head) || 0) +
               (parseFloat(item.commission_mentor) || 0) +
               (parseFloat(item.commission_ceo_pool) || 0) +
               (parseFloat(item.commission_moa) || 0);
    }
    return (parseFloat(item.commission_sales_executive) || 0) +
           (parseFloat(item.commission_team_leader) || 0) +
           (parseFloat(item.commission_sales_manager) || 0) +
           (parseFloat(item.commission_ceo_pool) || 0);
};

const CoursesPage = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterVisibility, setFilterVisibility] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [priceHistoryItem, setPriceHistoryItem] = useState(null);

    const canEdit = user?.role === 'super_admin' || user?.role === 'admin';

    useEffect(() => { fetchItems(); }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/course-catalog');
            setItems(res.data?.items || []);
        } catch (e) {
            toast.error('Failed to fetch catalog');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        return items.filter(i => {
            if (filterType !== 'all' && i.type !== filterType) return false;
            if (filterStatus === 'active' && !i.is_active) return false;
            if (filterStatus === 'inactive' && i.is_active) return false;
            if (filterVisibility !== 'all' && i.visibility !== filterVisibility) return false;
            if (searchTerm && !i.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
            return true;
        });
    }, [items, filterType, filterStatus, filterVisibility, searchTerm]);

    const allSelected = filtered.length > 0 && filtered.every(i => selectedIds.includes(i.id));

    const toggleAll = () => {
        setSelectedIds(allSelected ? [] : filtered.map(i => i.id));
    };

    const toggleOne = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleBulkAction = async (action) => {
        if (selectedIds.length === 0) return;
        if (action === 'delete' && !confirm(`Delete ${selectedIds.length} item(s)?`)) return;
        try {
            const res = await apiClient.post('/course-catalog/bulk-action', { ids: selectedIds, action });
            toast.success(res.data?.message || 'Done');
            setSelectedIds([]);
            fetchItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Bulk action failed');
        }
    };

    const resetForm = () => {
        setForm({ ...EMPTY_FORM });
        setEditItem(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.price) { toast.error('Name and price are required'); return; }
        const payload = {
            name: form.name,
            price: parseFloat(form.price),
            type: form.type,
            visibility: form.visibility || 'sales',
            commission_sales_executive: parseFloat(form.commission_sales_executive || 0),
            commission_team_leader: parseFloat(form.commission_team_leader || 0),
            commission_sales_manager: parseFloat(form.commission_sales_manager || 0),
            commission_cs_agent: parseFloat(form.commission_cs_agent || 0),
            commission_cs_head: parseFloat(form.commission_cs_head || 0),
            commission_mentor: parseFloat(form.commission_mentor || 0),
            commission_ceo_pool: parseFloat(form.commission_ceo_pool || 0),
            commission_moa: parseFloat(form.commission_moa || 0),
        };
        try {
            if (editItem) {
                await apiClient.put(`/course-catalog/${editItem.id}`, payload);
                toast.success('Item updated');
            } else {
                await apiClient.post('/course-catalog', payload);
                toast.success('Item added');
            }
            setShowModal(false);
            resetForm();
            fetchItems();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        }
    };

    const openEdit = (item) => {
        setEditItem(item);
        setForm({
            name: item.name,
            price: String(item.price),
            type: item.type,
            visibility: item.visibility || (item.type === 'addon' ? 'both' : item.type === 'upgrade' ? 'cs' : 'sales'),
            commission_sales_executive: String(item.commission_sales_executive || 0),
            commission_team_leader: String(item.commission_team_leader || 0),
            commission_sales_manager: String(item.commission_sales_manager || 0),
            commission_cs_agent: String(item.commission_cs_agent || 0),
            commission_cs_head: String(item.commission_cs_head || 0),
            commission_mentor: String(item.commission_mentor || 0),
            commission_ceo_pool: String(item.commission_ceo_pool || 0),
            commission_moa: String(item.commission_moa || 0),
        });
        setShowModal(true);
    };

    const toggleActive = async (item) => {
        const newStatus = !item.is_active;
        try {
            await apiClient.put(`/course-catalog/${item.id}`, { is_active: newStatus });
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_active: newStatus } : i));
            toast.success(`${item.name} ${newStatus ? 'activated' : 'deactivated'}`);
        } catch (e) {
            toast.error('Failed to update status');
        }
    };

    const typeBadge = (type) => {
        const opt = TYPE_OPTIONS.find(o => o.id === type);
        if (!opt) return <Badge variant="secondary">{type}</Badge>;
        return <Badge className={`${opt.color} text-white`}>{opt.label}</Badge>;
    };

    const counts = useMemo(() => {
        const c = { all: items.length, course: 0, addon: 0, upgrade: 0 };
        items.forEach(i => { if (c[i.type] !== undefined) c[i.type]++; });
        return c;
    }, [items]);

    const isUpgrade = form.type === 'upgrade';
    const formTotal = calcTotal(form);

    return (
        <div className="space-y-6" data-testid="courses-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Course Catalog</h1>
                    <p className="text-muted-foreground">Manage courses, add-ons, upgrade packages & commissions</p>
                </div>
                {canEdit && (
                    <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-catalog-item-btn">
                        <Plus className="h-4 w-4 mr-2" />Add Item
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                {['all', 'course', 'addon', 'upgrade'].map(t => (
                    <Button key={t} variant={filterType === t ? 'default' : 'outline'} size="sm"
                        onClick={() => setFilterType(t)} data-testid={`filter-type-${t}`}>
                        {t === 'all' ? 'All' : TYPE_OPTIONS.find(o => o.id === t)?.label}
                        <span className="ml-1.5 text-xs opacity-70">({counts[t] || 0})</span>
                    </Button>
                ))}
                <div className="flex-1" />
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 w-48" data-testid="catalog-search" />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-32" data-testid="filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterVisibility} onValueChange={setFilterVisibility}>
                    <SelectTrigger className="w-36" data-testid="filter-visibility">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Visibility</SelectItem>
                        <SelectItem value="sales">Sales Only</SelectItem>
                        <SelectItem value="cs">CS Only</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Bulk Actions */}
            {selectedIds.length > 0 && canEdit && (
                <div className="flex items-center gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg" data-testid="bulk-actions-bar">
                    <span className="text-sm font-medium">{selectedIds.length} selected</span>
                    <div className="flex-1" />
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('activate')} data-testid="bulk-activate-btn">
                        <Eye className="h-3.5 w-3.5 mr-1.5" />Activate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('deactivate')} data-testid="bulk-deactivate-btn">
                        <EyeOff className="h-3.5 w-3.5 mr-1.5" />Deactivate
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')} data-testid="bulk-delete-btn">
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>Clear</Button>
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="flex justify-center h-64 items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
                </div>
            ) : (
                <div className="border rounded-lg overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                {canEdit && (
                                    <TableHead className="w-10">
                                        <input type="checkbox" checked={allSelected} onChange={toggleAll}
                                            className="rounded border-gray-400 h-4 w-4" data-testid="select-all-checkbox" />
                                    </TableHead>
                                )}
                                <TableHead>Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Visible To</TableHead>
                                <TableHead className="text-right">Price</TableHead>
                                <TableHead className="text-right">SE Comm.</TableHead>
                                <TableHead className="text-right">TL Comm.</TableHead>
                                <TableHead className="text-right">SM Comm.</TableHead>
                                <TableHead className="text-right">CS Agent</TableHead>
                                <TableHead className="text-right">CS Head</TableHead>
                                <TableHead className="text-right">Mentor</TableHead>
                                <TableHead className="text-right">MOA</TableHead>
                                <TableHead className="text-right">CEO Pool</TableHead>
                                <TableHead className="text-right font-semibold">Total Out</TableHead>
                                <TableHead className="text-center">Status</TableHead>
                                {canEdit && <TableHead className="text-center w-16">Edit</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((item) => {
                                const total = calcTotal(item);
                                const isUpg = item.type === 'upgrade';
                                return (
                                    <TableRow key={item.id}
                                        className={`${selectedIds.includes(item.id) ? 'bg-primary/5' : ''} ${!item.is_active ? 'opacity-60' : ''}`}
                                        data-testid={`catalog-row-${item.id}`}>
                                        {canEdit && (
                                            <TableCell>
                                                <input type="checkbox" checked={selectedIds.includes(item.id)}
                                                    onChange={() => toggleOne(item.id)}
                                                    className="rounded border-gray-400 h-4 w-4" />
                                            </TableCell>
                                        )}
                                        <TableCell className="font-medium">{item.name}</TableCell>
                                        <TableCell>{typeBadge(item.type)}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={
                                                item.visibility === 'both' ? 'text-emerald-600 border-emerald-200 bg-emerald-50' :
                                                item.visibility === 'cs' ? 'text-amber-600 border-amber-200 bg-amber-50' :
                                                'text-blue-600 border-blue-200 bg-blue-50'
                                            } data-testid={`visibility-${item.id}`}>
                                                {item.visibility === 'both' ? 'Sales + CS' : item.visibility === 'cs' ? 'CS' : 'Sales'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-semibold">{fmtAED(item.price)}</TableCell>
                                        {/* Sales commissions */}
                                        <TableCell className="text-right text-sm">
                                            {!isUpg && item.commission_sales_executive ? fmtAED(item.commission_sales_executive) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {!isUpg && item.commission_team_leader ? fmtAED(item.commission_team_leader) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {!isUpg && item.commission_sales_manager ? fmtAED(item.commission_sales_manager) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        {/* CS / Mentor commissions (upgrade) */}
                                        <TableCell className="text-right text-sm">
                                            {isUpg && item.commission_cs_agent ? fmtAED(item.commission_cs_agent) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {isUpg && item.commission_cs_head ? fmtAED(item.commission_cs_head) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {isUpg && item.commission_mentor ? fmtAED(item.commission_mentor) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        {/* MOA: only on upgrades (CS closures) */}
                                        <TableCell className="text-right text-sm">
                                            {isUpg && item.commission_moa ? fmtAED(item.commission_moa) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        {/* CEO Pool: applies to both sales + upgrade */}
                                        <TableCell className="text-right text-sm">
                                            {item.commission_ceo_pool ? fmtAED(item.commission_ceo_pool) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        {/* Total out */}
                                        <TableCell className="text-right font-semibold text-primary">
                                            {total > 0 ? fmtAED(total) : <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {canEdit ? (
                                                <div className="flex items-center justify-center gap-1.5" data-testid={`toggle-active-${item.id}`}>
                                                    <Switch
                                                        checked={item.is_active !== false}
                                                        onCheckedChange={() => toggleActive(item)}
                                                        className="data-[state=checked]:bg-emerald-500"
                                                    />
                                                    <span className={`text-[10px] font-medium ${item.is_active !== false ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                                                        {item.is_active !== false ? 'Active' : 'Off'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <Badge className={item.is_active ? 'bg-emerald-500 text-white' : 'bg-gray-400 text-white'}>
                                                    {item.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        {canEdit && (
                                            <TableCell className="text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}
                                                        data-testid={`edit-item-${item.id}`}>
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-blue-600"
                                                        onClick={() => setPriceHistoryItem(item)}
                                                        data-testid={`price-history-${item.id}`}
                                                        title="Price History">
                                                        <History className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={canEdit ? 13 : 11} className="text-center py-12 text-muted-foreground">
                                        No items found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Add/Edit Modal */}
            <Dialog open={showModal} onOpenChange={(o) => { if (!o) { setShowModal(false); resetForm(); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editItem ? 'Edit Item' : 'Add Catalog Item'}</DialogTitle>
                        <DialogDescription>Course, add-on, or upgrade package with commission</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Advance Course" data-testid="catalog-item-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Price (AED) *</Label>
                                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    placeholder="5000" data-testid="catalog-item-price" />
                            </div>
                            <div className="space-y-2">
                                <Label>Type *</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="catalog-item-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TYPE_OPTIONS.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Visible To *</Label>
                            <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                                <SelectTrigger data-testid="catalog-item-visibility"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VISIBILITY_OPTIONS.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">Controls which teams can see this item in their dropdowns</p>
                        </div>

                        {/* Sales Commission Fields (for course/addon) */}
                        {!isUpgrade && (
                            <div className="pt-2 border-t">
                                <Label className="text-sm font-semibold text-muted-foreground mb-3 block">Sales Commission (AED per sale)</Label>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Sales Executive</Label>
                                        <Input type="number" value={form.commission_sales_executive}
                                            onChange={(e) => setForm({ ...form, commission_sales_executive: e.target.value })}
                                            placeholder="0" data-testid="commission-se" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Team Leader</Label>
                                        <Input type="number" value={form.commission_team_leader}
                                            onChange={(e) => setForm({ ...form, commission_team_leader: e.target.value })}
                                            placeholder="0" data-testid="commission-tl" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Sales Manager</Label>
                                        <Input type="number" value={form.commission_sales_manager}
                                            onChange={(e) => setForm({ ...form, commission_sales_manager: e.target.value })}
                                            placeholder="0" data-testid="commission-sm" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Upgrade Commission Fields (for upgrade type) */}
                        {isUpgrade && (
                            <div className="pt-2 border-t">
                                <Label className="text-sm font-semibold text-muted-foreground mb-3 block">Upgrade Commission (AED per sale)</Label>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">CS Agent</Label>
                                        <Input type="number" value={form.commission_cs_agent}
                                            onChange={(e) => setForm({ ...form, commission_cs_agent: e.target.value })}
                                            placeholder="0" data-testid="commission-cs-agent" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">CS Head</Label>
                                        <Input type="number" value={form.commission_cs_head}
                                            onChange={(e) => setForm({ ...form, commission_cs_head: e.target.value })}
                                            placeholder="0" data-testid="commission-cs-head" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">Mentor</Label>
                                        <Input type="number" value={form.commission_mentor}
                                            onChange={(e) => setForm({ ...form, commission_mentor: e.target.value })}
                                            placeholder="0" data-testid="commission-mentor" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div className="space-y-1.5">
                                        <Label className="text-xs">MOA (Master of Academics)</Label>
                                        <Input type="number" value={form.commission_moa}
                                            onChange={(e) => setForm({ ...form, commission_moa: e.target.value })}
                                            placeholder="0" data-testid="commission-moa" />
                                        <p className="text-[10px] text-muted-foreground">Only applies on CS upgrade closings.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CEO Pool — applies to both sales AND upgrade
                            closings, so it's rendered outside the
                            isUpgrade / !isUpgrade gates. Was previously
                            buried inside the upgrade-only block which
                            made it un-editable for sales courses. */}
                        <div className="pt-2 border-t">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label className="text-xs">CEO Pool (AED per sale)</Label>
                                    <Input type="number" value={form.commission_ceo_pool}
                                        onChange={(e) => setForm({ ...form, commission_ceo_pool: e.target.value })}
                                        placeholder="0" data-testid="commission-ceo-pool" />
                                    <p className="text-[10px] text-muted-foreground">Applies to both sales &amp; upgrade. Tracked as a running total — not paid to anyone individual.</p>
                                </div>
                            </div>
                        </div>

                        {/* Total Commission Out */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
                            <span className="text-sm font-medium">Total Commission Out</span>
                            <span className="text-lg font-bold font-mono text-primary" data-testid="total-commission-out">
                                {fmtAED(formTotal)}
                            </span>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
                            <Button type="submit" data-testid="catalog-item-submit">{editItem ? 'Update' : 'Add'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            {/* Price History Dialog */}
            <Dialog open={!!priceHistoryItem} onOpenChange={(o) => { if (!o) setPriceHistoryItem(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Price History</DialogTitle>
                        <DialogDescription>{priceHistoryItem?.name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {/* Current Price */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
                            <span className="text-sm font-medium">Current Price</span>
                            <span className="text-lg font-bold font-mono text-primary" data-testid="current-price">
                                {fmtAED(priceHistoryItem?.price)}
                            </span>
                        </div>
                        {/* History Log */}
                        {(!priceHistoryItem?.price_history || priceHistoryItem.price_history.length === 0) ? (
                            <p className="text-sm text-muted-foreground text-center py-6">No price changes recorded yet</p>
                        ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="price-history-list">
                                {[...(priceHistoryItem.price_history || [])].reverse().map((entry, idx) => {
                                    const increased = entry.new_price > entry.old_price;
                                    const diff = entry.new_price - entry.old_price;
                                    return (
                                        <div key={idx} className="flex items-center justify-between p-2.5 rounded-md border bg-card text-sm">
                                            <div className="space-y-0.5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-muted-foreground line-through">{fmtAED(entry.old_price)}</span>
                                                    <span className="text-muted-foreground">&rarr;</span>
                                                    <span className="font-mono font-semibold">{fmtAED(entry.new_price)}</span>
                                                    {increased ? (
                                                        <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">
                                                            <ArrowUp className="h-3 w-3 mr-0.5" />+{fmtAED(diff)}
                                                        </Badge>
                                                    ) : (
                                                        <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                                                            <ArrowDown className="h-3 w-3 mr-0.5" />{fmtAED(diff)}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    {new Date(entry.changed_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    {entry.changed_by && <span className="ml-1">by {entry.changed_by}</span>}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CoursesPage;
